import React, { ReactElement } from "react";
import { MarketsByLeague } from "../models/GamesResults";
import { Users } from "../models/User";
import { BetDetails, ParlaySelection } from "../models/MyBetModel";
import { Odd } from "../models/Odd_response_model.tsx";
import { Timestamp } from "firebase/firestore";
import { formatAdminFiguresDateTime } from "./adminFiguresTime.tsx";
import { formatFiguresDateTime } from "./figuresTime.tsx";

export function decimalToFraction(decimal: number): { numerator: number; denominator: number; display: ReactElement } {
    if (decimal === parseInt(decimal.toString())) { // Handle whole numbers
        return { numerator: parseInt(decimal.toString()), denominator: 1, display: (<><sup>{parseInt(decimal.toString())}</sup> / <sub>1</sub></>) };
    }

    const decimalStr = decimal.toString();
    const decimalPlaces = decimalStr.includes('.') ? decimalStr.split('.')[1].length : 0;

    let denominator = Math.pow(10, decimalPlaces);
    let numerator = Math.round(decimal * denominator); // Use Math.round to handle potential floating point inaccuracies

    // Find GCD to simplify the fraction
    function GCD(a: number, b: number): number {
        return b === 0 ? a : GCD(b, a % b);
    }

    const commonDivisor = GCD(Math.abs(numerator), denominator);
    numerator /= commonDivisor;
    denominator /= commonDivisor;

    return {
        numerator: numerator,
        denominator: denominator,
        display: (<><sup>{numerator}</sup> / <sub>{denominator}</sub></>)
    };
}

export const calcProfit = (stake: number, price: number): number => {

    if (price === undefined) {
        alert("we wont be calculating anything at this time");
        return 0;
    }

    let profit = 0;

    if (price < 0) {
        profit = (stake * 100) / Math.abs(price);
    } else if (price > 0) {
        profit = stake * (price / 100);
    }

    return Math.round(profit);

}

export const calcStakeFromToWin = (toWin: number, price: number): number => {
    if (price < 0) {
        return Math.round((toWin * Math.abs(price)) / 100);
    } else {
        return Math.round((toWin * 100) / price);
    }
};

export const formatWithCommas = (value?: number | string) => {
    if (!value) return "";
    return Number(value).toLocaleString("en-US");
};

export const parseFormattedNumber = (value: string): number => {
    return Number(value.replace(/,/g, ""));
};

export const calcStake = (profit: number, price: number): number => {

    let stake = 0;

    if (price > 0) {
        stake = (profit * 100) / price;
    } else {
        stake = (profit * Math.abs(price)) / 100;
    }
    return Math.round(stake);
}

export function slugify(str: string): string {
    str = str.replace(/^\s+|\s+$/g, ''); // Trim leading/trailing spaces
    str = str.toLowerCase(); // Convert to lowercase
    // Remove accents, swap ñ for n, etc.
    // This step uses String.prototype.normalize and a regex to remove diacritics
    str = str.normalize('NFD').replace(/[\u0300-\u036f]/g, '');
    // Remove invalid chars, replace spaces and multiple hyphens
    str = str.replace(/[^a-z0-9 -]/g, '') // Remove invalid characters
        .replace(/\s+/g, '-')    // Replace spaces with hyphens
        .replace(/-+/g, '-');    // Collapse multiple hyphens
    return str;
}

export const useClickAnimation = () => {
    const animate = (event: any) => {
        const el = event.currentTarget;

        el.style.transition = "transform 80ms ease, opacity 80ms ease";
        el.style.transform = "scale(0.40)";
        el.style.opacity = "0.85";

        setTimeout(() => {
            el.style.transform = "scale(1)";
            el.style.opacity = "1";
        }, 70);
    };

    return animate;
};

export function getUniqueMarketIdsByLeague(games: { league: { id: string; }; odds?: { market_id?: string; }[] }[]): MarketsByLeague {
    const result: Record<string, Set<string>> = {};

    games.forEach(game => {
        const leagueId = game.league?.id;
        if (!leagueId || !Array.isArray(game.odds)) return;

        if (!result[leagueId]) {
            result[leagueId] = new Set<string>();
        }

        game.odds.forEach(odd => {
            if (odd.market_id) {
                result[leagueId].add(odd.market_id);
            }
        });
    });

    const marketsResult: MarketsByLeague = {};

    Object.keys(result).forEach(leagueId => {
        marketsResult[leagueId] = Array.from(result[leagueId]);
    });

    return marketsResult;
}

export const formatGainLimitDisplay = (maxGainLimit: number) =>
    `$${maxGainLimit.toLocaleString("en-US")}`;

/** maxGainLimit <= 0 means unlimited (same convention as an agent's max payout). */
export const exceedsGainLimit = (rewardAmount: number, maxGainLimit: number) =>
    maxGainLimit > 0 && rewardAmount > maxGainLimit;

/** Stable key for one parlay/teaser leg (Over vs Under are different). */
export const getMultiLegSelectionKey = (selection: any): string => {
    const id = String(selection?.id_selection ?? selection?.odd?.id ?? "").trim();
    if (id) return id;

    const fixture = String(selection?.fixture_id ?? "").trim();
    const market = String(
        selection?.marketName ?? selection?.odd?.market ?? selection?.odd?.market_id ?? ""
    ).trim().toLowerCase();
    const name = String(selection?.betName ?? selection?.odd?.name ?? "").trim().toLowerCase();
    const line = String(
        selection?.odd?.selection_line ?? selection?.selectionType ?? ""
    ).trim().toLowerCase();
    const points = selection?.odd?.points ?? "";
    return `${fixture}|${market}|${name}|${line}|${points}`;
};

/** Signature of the full multi-leg ticket; used to stack only identical parlays/teasers. */
export const getMultiLegBetSignature = (selections?: any[]): string =>
    (selections ?? [])
        .map(getMultiLegSelectionKey)
        .filter(Boolean)
        .sort()
        .join("||");

/** Key for a straight bet selection. Over/Under and different markets do not share limits. */
export const getStraightBetKey = (bet: any): string => {
    const fixture = String(bet?.fixture_id ?? "").trim();
    const gameId = String(bet?.gameId ?? "").trim();
    const betType = String(bet?.betType ?? "").trim().toLowerCase();
    const gameType = String(bet?.gameType ?? "").trim().toLowerCase();
    const oddId = String(bet?.odd?.id ?? bet?.id_selection ?? "").trim();

    if (oddId) {
        return `${fixture}|${gameId}|${betType}|${gameType}|${oddId}`;
    }

    const market = String(
        bet?.marketName ?? bet?.odd?.market ?? bet?.odd?.market_id ?? ""
    ).trim().toLowerCase();
    const name = String(bet?.betName ?? bet?.odd?.name ?? "").trim().toLowerCase();
    const line = String(
        bet?.odd?.selection_line ?? bet?.selectionType ?? ""
    ).trim().toLowerCase();
    const points = bet?.odd?.points ?? "";
    const teamId = String(bet?.teamId ?? "").trim();
    return `${fixture}|${gameId}|${betType}|${gameType}|${market}|${name}|${line}|${points}|${teamId}`;
};

export const isSameStraightBet = (a: any, b: any): boolean =>
    !!a && !!b && getStraightBetKey(a) === getStraightBetKey(b);

export const getBetOddPrice = (bet: any): number =>
    Number(bet?.odd?.price ?? bet?.price ?? 0);

/** + odds → wager limit validates risk; - odds → wager limit validates to-win. */
export const wagerLimitAppliesToRisk = (price: number) => price > 0;

export const getLimitsByGameType = (user: Users, gameType?: string) => {
    const type = String(gameType ?? "").toLowerCase();

    // propslive / anything with "live" uses the live wager limit
    if (type === "live" || type === "propslive" || type.includes("live")) {
        return {
            maxWager: user?.liveMaxWager || 0,
        };
    }

    if (type === "props") {
        return {
            maxWager: user?.propsMaxWager || 0,
        };
    }

    if (type === "contest") {
        return {
            maxWager: user?.contestMaxWager || 0,
        };
    }

    return {
        maxWager: user?.regularBetMaxWager || 0,
    };
};

/** Raw wager-limit amount for the bet type (0 = unlimited). */
export const getWagerLimitForBet = (user: Users, bet?: any): number => {
    if (!bet) return 0;

    const betType = bet.betType?.toLowerCase();
    const gameType = String(bet.gameType ?? "").toLowerCase();
    const isLiveGame =
        gameType === "live" ||
        gameType === "propslive" ||
        gameType.includes("live");

    if (betType === "contest") {
        return Number(user?.contestMaxWager ?? 0);
    }

    if (betType === "straight") {
        if (isLiveGame) {
            return Number(user?.liveMaxWager ?? 0);
        }

        if (gameType === "props") {
            return Number(user?.propsMaxWager ?? 0);
        }

        return Number(user?.regularBetMaxWager ?? 0);
    }

    if (betType === "parlay") {
        return Number(user?.parlayMaxWager ?? 0);
    }

    if (betType === "teaser") {
        return Number(user?.teaserMaxWager ?? 0);
    }

    // Fallback by gameType (covers propslive → live)
    return getLimitsByGameType(user, bet.gameType).maxWager || 0;
};

export const exceedsWagerLimit = (user: Users, bet: BetDetails) => {
    const maxWager = getWagerLimitForBet(user, bet);
    if (!(maxWager > 0)) return false;

    const price = getBetOddPrice(bet);
    const betAmount = bet.bet ?? 0;
    const rewardAmount = bet.reward ?? 0;

    if (wagerLimitAppliesToRisk(price)) {
        return betAmount > maxWager;
    }

    return rewardAmount > maxWager;
};


export const getLimitErrorMessage = (user: Users, bet: BetDetails, maxGainLimit = 0) => {
    const maxWager = getWagerLimitForBet(user, bet);
    const betAmount = bet.bet ?? 0;
    const rewardAmount = bet.reward ?? 0;
    const price = getBetOddPrice(bet);

    if (maxWager > 0) {
        if (wagerLimitAppliesToRisk(price) && betAmount > maxWager) {
            return `Your bet of $${betAmount.toLocaleString("en-US")} exceeds the wager limit of $${maxWager.toLocaleString("en-US")}.`;
        }

        if (!wagerLimitAppliesToRisk(price) && rewardAmount > maxWager) {
            return `Your bet of $${rewardAmount.toLocaleString("en-US")} exceeds the wager limit of $${maxWager.toLocaleString("en-US")}.`;
        }
    }

    // To-win is always capped by agent max payout when validating win side / overall gain
    if (exceedsGainLimit(rewardAmount, maxGainLimit)) {
        return `The gain limit is ${formatGainLimitDisplay(maxGainLimit)}`;
    }

    return null;
};

export const formatDateTime = (dateString: string, userLocale?: string) => {
    const date = new Date(dateString);

    // Si no se pasa locale, toma el del navegador
    const locale = userLocale || navigator.language;

    return {
        date: date.toLocaleDateString(locale, {
            day: '2-digit',
            month: '2-digit',
            year: '2-digit', // o 'numeric' si quieres 4 dígitos
        }),
        time: date.toLocaleTimeString(locale, {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true // para que sea AM/PM
        })
    };
};

/** Insert thousand separators into numbers embedded in a label (e.g. "Under 1049.5" → "Under 1,049.5"). */
export function formatNumbersInLabel(label: string | null | undefined): string {
    if (!label) return "";
    return label.replace(/\b\d+(\.\d+)?\b/g, (raw) => {
        const n = Number(raw);
        if (!Number.isFinite(n)) return raw;
        return n.toLocaleString("en-US", {
            maximumFractionDigits: 2,
            minimumFractionDigits: raw.includes(".") ? (raw.split(".")[1]?.length ?? 0) : 0,
        });
    });
}

type TeaserOddLike = {
    points?: number;
    name?: string;
    selection?: string;
    normalized_selection?: string;
    selection_line?: string | null;
};

function isTeaserOverLine(selectionLine?: string | null): boolean {
    const sel = String(selectionLine ?? "").toLowerCase().trim();
    return sel.includes("over") || sel.startsWith("o");
}

function isTeaserTotalLine(selectionLine?: string | null): boolean {
    const sel = String(selectionLine ?? "").toLowerCase().trim();
    return sel.includes("over") || sel.includes("under") || sel.startsWith("o") || sel.startsWith("u");
}

function formatTeaserLineNumber(points: number, selectionLine?: string | null): string {
    if (isTeaserTotalLine(selectionLine) || points === 0) return String(points);
    if (points > 0) return `+${points}`;
    return String(points);
}

function replaceTeaserLineInLabel(label: string | undefined, line: string): string {
    const value = String(label ?? "").trim();
    if (!value) return line;
    if (!/-?\d+(?:\.\d+)?/.test(value)) return `${value} ${line}`.trim();
    return value.replace(/\s*[+-]?\d+(?:\.\d+)?\s*$/, ` ${line}`).trim();
}

function selectionHasLineNumber(selection?: string): boolean {
    return /-?\d+(?:\.\d+)?/.test(String(selection ?? "").trim());
}

/** Keep odd.name aligned with the teased points the grader reads. */
export function syncTeaserOddName<T extends TeaserOddLike>(odd: T): T {
    if (typeof odd.points !== "number" || !Number.isFinite(odd.points)) return odd;
    const line = formatTeaserLineNumber(odd.points, odd.selection_line);
    return {
        ...odd,
        name: replaceTeaserLineInLabel(odd.name, line),
        ...(odd.selection && selectionHasLineNumber(odd.selection)
            ? { selection: replaceTeaserLineInLabel(odd.selection, line) }
            : {}),
    };
}

/** Teaser moves the line and the labels the grader reads (`odd.name` / betName). */
export function applyTeaserPointsToOdd<T extends TeaserOddLike>(odd: T, teaserPoints: number): T {
    const adjusted = { ...odd };
    if (typeof adjusted.points !== "number" || !Number.isFinite(adjusted.points)) {
        return adjusted;
    }

    adjusted.points = isTeaserOverLine(adjusted.selection_line)
        ? adjusted.points - teaserPoints
        : adjusted.points + teaserPoints;

    return syncTeaserOddName(adjusted);
}

export function formatNumber(
    value: number | null | undefined,
    selectionLine?: string,
    type: "price" | "points" | "unsigned" = "price",
): string {

    if (value == null) return "";

    const line = selectionLine?.toLowerCase() ?? "";
    const formatted = String(value);

    if (type === "unsigned") {
        return formatted;
    }

    if (type === "points") {
        if (value === 0) return "EV";
        if (line.includes("over")) return `O ${formatted}`;
        if (line.includes("under")) return `U ${formatted}`;
        if (value > 0) return `+${formatted}`;
        return formatted;
    }
    if (value > 0) return `+${formatted}`;

    return formatted;
}

export function formatWithSign(value: number | null | undefined, selectionLine?: string, type: "price" | "points" | "unsigned" = "price"): string {
    if (value == null) return "";

    const line = selectionLine?.toLowerCase() ?? "";
    const formatted = String(value);

    if (type === "unsigned") {
        return formatted;
    }

    if (type === "points") {
        if (value === 0) return "EV";
        if (line.includes("over")) return `O ${formatted}`;
        if (line.includes("under")) return `U ${formatted}`;
        if (value > 0) return `+${formatted}`;
        return formatted;
    }

    if (value > 0) return `+${formatted}`;
    return formatted;
}

/** Full bet line for slips: "O 3.5 -130", "U 4.5 -115", "3.5 -130", or "-130". */
export function formatBetLine(
    odd?: { points?: number | null; price?: number | null; selection_line?: string | null; market_id?: string | null } | null,
    marketName?: string | null,
    pointsOnly = false,
): string {
    if (!odd) return "";

    const isMoneyline =
        String(marketName ?? "").toLowerCase().includes("moneyline") ||
        String(odd.market_id ?? "").toLowerCase() === "moneyline";
    const price = !pointsOnly && odd.price != null ? formatNumber(odd.price) : "";
    if (isMoneyline || odd.points == null) return price;

    const line = String(odd.selection_line ?? "").toLowerCase();
    if (line === "over" || line.startsWith("over")) return `O ${odd.points} ${price}`.trim();
    if (line === "under" || line.startsWith("under")) return `U ${odd.points} ${price}`.trim();

    const points = formatNumber(odd.points, undefined, "points");
    return [points, price].filter(Boolean).join(" ");
}

export function formatBetScheduled(gameDate?: Timestamp | Date | null): string {
    if (!gameDate) return "";
    const date = typeof (gameDate as Timestamp).toDate === "function"
        ? (gameDate as Timestamp).toDate()
        : gameDate instanceof Date
            ? gameDate
            : null;
    if (!date || Number.isNaN(date.getTime())) return "";
    return formatFiguresDateTime(date);
}

export const formatNumbers = (value: any) => {
    const num = Number(value);
    if (isNaN(num)) return '';

    return Math.round(num).toLocaleString('en-US');
};

export const formatCurrency = (value: any, decimals = 2) => {
    const num = Number(value);
    if (isNaN(num)) return (0).toFixed(decimals);

    return num.toLocaleString('en-US', {
        minimumFractionDigits: decimals,
        maximumFractionDigits: decimals,
    });
};


export const ALLOWED_MARKETS = new Set([
    "1st half",
    "2nd half",
    "1st quarter",
    "2nd quarter",
    "3rd quarter",
    "4th quarter",
    "1st period",
    "2nd period",
    "3rd period"
]);

export const extractPeriod = (name: string) => {
    if (!name) return "";

    const normalized = name.toLowerCase();

    if (normalized.includes("1st half")) return "1st half";
    if (normalized.includes("2nd half")) return "2nd half";

    if (normalized.includes("1st quarter")) return "1st quarter";
    if (normalized.includes("2nd quarter")) return "2nd quarter";
    if (normalized.includes("3rd quarter")) return "3rd quarter";
    if (normalized.includes("4th quarter")) return "4th quarter";

    if (normalized.includes("1st period")) return "1st period";
    if (normalized.includes("2nd period")) return "2nd period";
    if (normalized.includes("3rd period")) return "3rd period";

    return "";
};

export const formatMarketTitle = (title?: string, league?: string) => {
    if (!title) return "";

    const cleanTitle = title.trim().toLowerCase();
    const normalizedLeague = league?.toUpperCase();

    if (normalizedLeague === "MLB") {
        if (cleanTitle.includes("1st half")) return "1st 5";
        if (cleanTitle.includes("2nd half")) return "L5";
    }

    const periodMap: Record<string, string> = {
        "1st quarter": "1Q",
        "2nd quarter": "2Q",
        "3rd quarter": "3Q",
        "4th quarter": "4Q",

        "1st half": "1H",
        "2nd half": "2H",

        "1st period": "1P",
        "2nd period": "2P",
        "3rd period": "3P",

        // "full game": "FG",
        // "game": "FG"
    };

    for (const key in periodMap) {
        if (cleanTitle.includes(key)) {
            return periodMap[key];
        }
    }
    const words = title.trim().split(/\s+/);
    if (words.length < 2) return "";

    const number = words[0].match(/\d+/)?.[0] || words[0];
    const secondInitial = words[1][0];

    return `${number}${secondInitial}`;
};

/** Suffix next to team names on half/quarter/period boards, e.g. " 1h", " 1q". */
export const getPeriodTeamSuffix = (market?: string, league?: string): string => {
    if (!market) return "";
    const period = extractPeriod(market);
    if (!period || !ALLOWED_MARKETS.has(period)) return "";
    const title = formatMarketTitle(market, league);
    if (!title) return "";
    if (/^\d+[hqp]$/i.test(title)) {
        return ` ${title.toLowerCase()}`;
    }
    return ` ${title}`;
};

export function PeriodTeamSuffixLabel({ market, league }: { market?: string; league?: string }) {
    const suffix = getPeriodTeamSuffix(market, league).trim();
    if (!suffix) return null;
    return <span className="period-team-suffix">{suffix}</span>;
}

export const getDisplayBetInfo = (bet: any) => {
    if (!bet) return { name: "", hideMarket: false };

    const market = bet?.marketName?.toLowerCase() || "";
    const gameType = String(bet?.gameType ?? "").toLowerCase();
    const isPropsLike = gameType === "props" || gameType === "propslive";
    const isTeamTotal = market.includes("team_total") || market.includes("team total");
    const isGameTotal = market.includes("total") && !isTeamTotal;

    const isOverUnder =
        bet?.odd?.selection_line?.toLowerCase() === "over" ||
        bet?.odd?.selection_line?.toLowerCase() === "under";

    const shouldReplaceName = isGameTotal && isOverUnder;

    const useFullName = shouldUseFullTeamName(bet?.league);
    const getTeamLabel = (str?: string) => {
        if (!str) return "";
        if (isSoccerLeague(bet?.league)) return str.trim();
        return shortenTeamDisplayName(str, useFullName);
    };

    // Specials / props: show the selection name, not just the team
    if (isPropsLike) {
        return {
            name: bet?.betName || bet?.odd?.selection || bet?.odd?.name || bet?.teamId || "",
            hideMarket: false,
        };
    }

    const periodSuffix = getPeriodTeamSuffix(bet?.marketName || bet?.odd?.market, bet?.league);

    return {
        name: (shouldReplaceName
            ? `${getTeamLabel(bet?.teamId)} / ${getTeamLabel(bet?.opposingTeam)}`
            : bet?.teamId || "") + periodSuffix,
        hideMarket: shouldReplaceName,
    };
};

export function findMatchingLiveOdd(
    gameOdds: any[],
    bet: { id_selection?: string; betName?: string; odd?: any }
): any | undefined {
    const currentOdd = bet.odd;
    const marketId = currentOdd?.market_id;
    if (!marketId) return undefined;

    const sameMarketOdds = gameOdds.filter((o: any) => o.market_id === marketId);
    if (sameMarketOdds.length === 0) return undefined;

    const byId = sameMarketOdds.find(
        (o: any) => o.id === bet.id_selection || o.id === currentOdd?.id
    );
    if (byId) return byId;

    if (currentOdd?.player_id || currentOdd?.team_id || currentOdd?.selection_line) {
        const bySide = sameMarketOdds.find((o: any) => isSameSelectionSide(currentOdd, o));
        if (bySide) return bySide;
    }

    return sameMarketOdds.find(
        (o: any) =>
            o.name === currentOdd?.name ||
            o.name === bet.betName ||
            o.selection === bet.betName
    );
}

export function matchesBetForOddChange(
    changed: { id_selection?: string; fixture_id?: string; gameId?: string; marketName?: string; odd?: any },
    bet: { id_selection?: string; fixture_id?: string; gameId?: string; marketName?: string; odd?: any }
): boolean {
    if (changed.id_selection === bet.id_selection) return true;
    if (changed.odd?.id && changed.odd.id === bet.odd?.id) return true;

    if (changed.fixture_id !== bet.fixture_id) return false;
    if (changed.odd?.market_id !== bet.odd?.market_id) return false;

    if (changed.odd && bet.odd && (bet.odd.player_id || bet.odd.team_id || bet.odd.selection_line)) {
        return isSameSelectionSide(bet.odd, changed.odd);
    }

    return (
        changed.gameId === bet.gameId &&
        changed.marketName === bet.marketName &&
        changed.odd?.normalized_selection === bet.odd?.normalized_selection
    );
}

export function isLiveBet(bet?: { gameType?: string } | null): boolean {
    return !!bet?.gameType?.toLowerCase().includes("live");
}

/** Recalculate stake/reward and apply a server-returned newOdd onto a bet. */
export function applyNewOddToBet<T extends {
    bet?: number;
    reward?: number;
    odd?: any;
    betName?: string;
    id_selection?: string;
}>(bet: T, newOdd: any): T {
    if (!newOdd) return bet;

    const oldPrice = bet.odd?.price ?? 0;
    const newPrice = newOdd.price ?? 0;
    const improved = oldPrice < 0 ? newPrice > oldPrice : newPrice > oldPrice;

    let newBet = bet.bet ?? 0;
    let newReward = bet.reward ?? 0;

    if (newPrice > 0) {
        newReward = calcProfit(newBet, newPrice);
    } else {
        newBet = calcStake(newReward, newPrice);
    }

    return {
        ...bet,
        id_selection: newOdd.id ?? bet.id_selection,
        odd: newOdd,
        betName: newOdd.name ?? bet.betName,
        bet: parseFloat(Number(newBet).toFixed(2)),
        reward: parseFloat(Number(newReward).toFixed(2)),
        oddsImproved: improved,
    };
}

export function getClientTimezone() {
    const offsetMinutes = new Date().getTimezoneOffset();
    const sign = offsetMinutes > 0 ? '-' : '+';
    const absoluteOffset = Math.abs(offsetMinutes);
    const hours = Math.floor(absoluteOffset / 60);
    const minutes = absoluteOffset % 60;

    // Pad with leading zeros if necessary
    const formattedHours = String(hours).padStart(2, '0');
    const formattedMinutes = String(minutes).padStart(2, '0');

    return `${sign}${formattedHours}:${formattedMinutes}`;
}

export const getMaxBetForBet = (user: Users, bet?: any) => {
    const limit = getWagerLimitForBet(user, bet);
    if (!(limit > 0)) return 0;

    const price = getBetOddPrice(bet);
    // For - odds the limit is on to-win, so convert to the equivalent max risk
    if (!wagerLimitAppliesToRisk(price) && price !== 0) {
        return Math.max(0, calcStake(limit, price));
    }

    return limit;
};

export const fullNameLeagues = ["NCAAB", "NCAAF", "USA - MLB All Star"];

const SOCCER_LEAGUE_MARKERS = [
    "soccer",
    "epl",
    "premier league",
    "la liga",
    "laliga",
    "serie a",
    "bundesliga",
    "ligue 1",
    "ligue1",
    " mls",
    "mls",
    "uefa",
    "champions league",
    "europa league",
    "conference league",
    "fa cup",
    "efl",
    "championship",
    "league one",
    "league two",
    "copa",
    "libertadores",
    "sudamericana",
    "eredivisie",
    "liga mx",
    "primeira",
    "super lig",
    "pro league",
    "saudi",
    "a-league",
    "j-league",
    "k-league",
    "world cup",
    "nations league",
    "primera division",
    "segunda",
];

export const isSoccerLeague = (league?: string) => {
    if (!league) return false;
    const normalized = league.trim().toLowerCase();
    if (normalized.includes("soccer")) return true;
    if (normalized === "mls" || normalized.endsWith(" mls") || normalized.includes(" - mls")) return true;
    return SOCCER_LEAGUE_MARKERS.some(marker => normalized.includes(marker));
};

/** Two-word nicknames that must not be shortened to the last word only. */
export const TWO_WORD_TEAM_NAMES = [
    { team: "Red Sox", sport: "Baseball" },
    { team: "White Sox", sport: "Baseball" },
    { team: "Blue Jays", sport: "Baseball" },
    { team: "Trail Blazers", sport: "Basketball" },
    { team: "Maple Leafs", sport: "Hockey" },
    { team: "Red Wings", sport: "Hockey" },
    { team: "Blue Jackets", sport: "Hockey" },
    { team: "Golden Knights", sport: "Hockey" },
] as const;

const TWO_WORD_TEAM_NAME_SET = new Set(
    TWO_WORD_TEAM_NAMES.map(({ team }) => team.toLowerCase())
);

export const shouldUseFullTeamName = (league?: string) => {
    if (!league) return false;
    const normalized = league.trim().toLowerCase();
    return fullNameLeagues.some(l => l.toLowerCase() === normalized);
};

const collegeTokenInitial = (word: string): string => {
    const letters = word.replace(/[^A-Za-z]/g, "");
    if (!letters) return word;
    return letters[0].toUpperCase();
};

/** NCAAF/NCAAB: keep 1–2 word names, compact longer ones so they fit one line. */
export const compactCollegeTeamName = (text?: string): string => {
    if (!text) return "";
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "";
    if (words.length <= 2) return words.join(" ");

    const last = words[words.length - 1];
    const initials = words.slice(0, -1).map(collegeTokenInitial).join("");
    return `${initials} ${last}`;
};

/** Short team label: last word, unless it is a known two-word nickname. */
export const shortenTeamDisplayName = (text?: string, useFullName = false): string => {
    if (!text) return "";
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length === 0) return "";
    if (useFullName) return compactCollegeTeamName(words.join(" "));
    if (words.length === 1) return words[0];

    const twoWord = `${words[words.length - 2]} ${words[words.length - 1]}`;
    if (TWO_WORD_TEAM_NAME_SET.has(twoWord.toLowerCase())) {
        return twoWord;
    }

    const teamLast = words[words.length - 1];
    const teamSecondLast = words[words.length - 2];

    if (teamLast.length <= 3 && words.length >= 2 && teamSecondLast && teamSecondLast.length > 3) {
        return teamSecondLast;
    }

    return teamLast;
};

export function tryParseFloat(str: string) {
    const value = parseFloat(str);
    if (isNaN(value)) {
        // console.log(`Could not parse "${str}" as a float. Returning null.`);
        return null;
    } else {
        return value;
    }
}

export const getPlayerName = (betName: string) => {
    const words = betName.split(" ");
    const stopIndex = words.findIndex(
        w => w.toLowerCase() === "over" || w.toLowerCase() === "under"
    );

    return stopIndex !== -1
        ? words.slice(0, stopIndex).join(" ")
        : words.slice(0, 2).join(" ");
};



export const shouldShowMarketTitle = (market?: string, period?: string) => {
    const text = `${market ?? ""} ${period ?? ""}`.toLowerCase();

    return (
        text.includes("half") ||
        text.includes("quarter") ||
        text.includes("period")
    );
};


type DateLike = Timestamp | Date | { seconds?: number; toDate?: () => Date } | null | undefined;

export const toBetDate = (value: DateLike): Date | null => {
    if (!value) return null;
    if (typeof (value as Timestamp).toDate === 'function') return (value as Timestamp).toDate();
    if ((value as { seconds?: number }).seconds !== undefined) {
        return new Date((value as { seconds: number }).seconds * 1000);
    }
    if (value instanceof Date) return value;
    return null;
};

const getSelectionGradingDate = (selection?: ParlaySelection): Date | null =>
    toBetDate(selection?.lastUpdateBySystem);

const getSelectionFiguresDate = (selection?: ParlaySelection): Date | null =>
    getSelectionGradingDate(selection) ?? toBetDate(selection?.gameDate);

const getFirstLostSelection = (selections: ParlaySelection[]): ParlaySelection | undefined => {
    const lost = selections.filter(s => s.grader?.result === 'Lost');
    if (lost.length === 0) return undefined;
    if (lost.length === 1) return lost[0];

    return lost.reduce<ParlaySelection>((earliest, selection) => {
        const selectionGameDate = toBetDate(selection.gameDate);
        const earliestGameDate = toBetDate(earliest.gameDate);
        if (!selectionGameDate) return earliest;
        if (!earliestGameDate || selectionGameDate < earliestGameDate) return selection;
        return earliest;
    }, lost[0]);
};

export const MULTI_LEG_BET_TYPES = ['Parlay', 'Teaser'] as const;

export const isBetWinResult = (result?: string | null): boolean =>
    result === 'Won' || result === 'Half Won';

export const isBetLossResult = (result?: string | null): boolean =>
    result === 'Lost' || result === 'Half Lost';

export const getBetWinAmount = (reward = 0, result?: string | null): number => {
    if (result === 'Won') return reward;
    if (result === 'Half Won') return reward;
    return 0;
};

export const getBetLossAmount = (stake = 0, result?: string | null): number => {
    if (result === 'Lost') return stake;
    if (result === 'Half Lost') return stake;
    return 0;
};

export const getBetResultDelta = (
    bet: Pick<BetDetails, 'bet' | 'reward' | 'grader'>,
    result?: string | null
): number => {
    const resolvedResult = result ?? bet.grader?.result;
    const winAmount = getBetWinAmount(bet.reward ?? 0, resolvedResult);
    if (winAmount > 0) return winAmount;

    const lossAmount = getBetLossAmount(bet.bet ?? 0, resolvedResult);
    if (lossAmount > 0) return -lossAmount;

    return 0;
};

export const getBetFiguresDate = (bet: BetDetails): Date | null => {
    const isMultiLeg = MULTI_LEG_BET_TYPES.includes(bet.betType as typeof MULTI_LEG_BET_TYPES[number]);
    const fallbackDate = () =>
        isMultiLeg
            ? (toBetDate(bet.lastUpdateBySystem) ?? toBetDate(bet.gameDate))
            : toBetDate(bet.lastUpdateBySystem);

    if (isMultiLeg && Array.isArray(bet.selections) && bet.selections.length > 0) {
        const firstLost = getFirstLostSelection(bet.selections);
        if (firstLost) {
            // Lost: usar gameDate de la pierna (no lastUpdateBySystem), para no
            // mover la pérdida a la semana siguiente si el grader corre al día siguiente.
            return toBetDate(firstLost.gameDate) ?? getSelectionFiguresDate(firstLost);
        }

        const result = bet.grader?.result;

        if (isBetWinResult(result)) {
            let latestGradingDate: Date | null = null;
            for (const selection of bet.selections) {
                const selectionDate = getSelectionFiguresDate(selection);
                if (selectionDate && (!latestGradingDate || selectionDate > latestGradingDate)) {
                    latestGradingDate = selectionDate;
                }
            }
            if (latestGradingDate) return latestGradingDate;
            return fallbackDate();
        }

        let latestGradedDate: Date | null = null;
        for (const selection of bet.selections) {
            if (!selection.grader?.result) continue;
            const selectionDate = getSelectionFiguresDate(selection);
            if (selectionDate && (!latestGradedDate || selectionDate > latestGradedDate)) {
                latestGradedDate = selectionDate;
            }
        }
        if (latestGradedDate) return latestGradedDate;
    }

    return fallbackDate();
};

/** Player history calendar day: lastUpdateBySystem in the viewer's local TZ. */
export const getPlayerHistoryDate = (bet: BetDetails): Date | null => {
    return toBetDate(bet.lastUpdateBySystem) ?? toBetDate(bet.gameDate) ?? toBetDate(bet.betDate);
};

export const isWithinDateRange = (date: Date | null, start: Date, end: Date): boolean => {
    if (!date) return false;
    const ms = date.getTime();
    return ms >= start.getTime() && ms <= end.getTime();
};

export const filterBetsByFiguresDateRange = <T extends BetDetails>(
    bets: T[],
    start: Date,
    end: Date
): T[] => bets.filter(bet => isWithinDateRange(getBetFiguresDate(bet), start, end));


export const widenBetQueryDateRange = (start: Date, end: Date): { queryStart: Date; queryEnd: Date } => {
    const queryStart = new Date(start);
    queryStart.setDate(queryStart.getDate() - 14);
    // +14 días: late grading; NO extender hasta "ahora" (eso hace lentísimas las semanas viejas).
    const queryEnd = new Date(end);
    queryEnd.setDate(queryEnd.getDate() + 14);
    return { queryStart, queryEnd };
};

export const widenBetGameDateQueryRange = (start: Date, end: Date): { queryStart: Date; queryEnd: Date } => {
    const queryStart = new Date(start);
    queryStart.setDate(queryStart.getDate() - 90);
    const queryEnd = new Date(end);
    queryEnd.setDate(queryEnd.getDate() + 14);
    return { queryStart, queryEnd };
};

export const mergeBetQueryDocs = (
    ...snapshots: Array<{ docs: Array<{ id: string; data: () => unknown }> }>
): Array<{ id: string; data: () => unknown }> => {
    const seen = new Set<string>();
    const docs: Array<{ id: string; data: () => unknown }> = [];
    for (const snapshot of snapshots) {
        for (const doc of snapshot.docs) {
            if (seen.has(doc.id)) continue;
            seen.add(doc.id);
            docs.push(doc);
        }
    }
    return docs;
};

export const isSameCalendarDay = (a: Date, b: Date): boolean =>
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate();

export const getLastDateOfToday = (dates: Date[]) => {
    const today = new Date();

    const todayDates = dates.filter(date =>
        date.getFullYear() === today.getFullYear() &&
        date.getMonth() === today.getMonth() &&
        date.getDate() === today.getDate()
    );

    if (todayDates.length === 0) return today;

    return new Date(Math.max(...todayDates.map(d => d.getTime())));
};

/** Admin-facing timestamp format (always America/Los_Angeles). */
export const formatTimestamp = (timestamp: Timestamp) => {
    if (timestamp && timestamp.seconds !== undefined) {
        return formatAdminFiguresDateTime(new Date(timestamp.seconds * 1000));
    } else {
        return;
    }
};

/** Mainline markets that can appear inside the props UI but should not be stored/shown as props. */
export function isMainlineMarket(marketName?: string | null): boolean {
    const market = String(marketName ?? "").toLowerCase().trim();
    if (!market) return false;

    if (market.includes("moneyline") || market.includes("money line") || market === "ml") return true;

    if (
        market.includes("point_spread") ||
        market.includes("point spread") ||
        market.includes("run_line") ||
        market.includes("run line") ||
        market.includes("puck_line") ||
        market.includes("puck line") ||
        /(^|\s)spreads?(\s|$)/.test(market)
    ) {
        return true;
    }

    if (market.includes("team_total") || market.includes("team total")) return true;

    if (
        market === "total" ||
        market === "total_points" ||
        market === "total_runs" ||
        market === "total_goals" ||
        market === "total points" ||
        market === "total runs" ||
        market === "total goals" ||
        /^(?:\d+(?:st|nd|rd|th)\s+(?:half|quarter|period)\s+)?total(?:\s+(?:points|runs|goals))?$/.test(market) ||
        /^(?:\d+(?:st|nd|rd|th)_)?(?:half|quarter|period)_total_(?:points|runs|goals)$/.test(market) ||
        /^total_(?:points|runs|goals)$/.test(market)
    ) {
        return true;
    }

    return false;
}

/**
 * Normalize market_id by stripping half/quarter/period prefixes.
 * e.g. "1st_half_moneyline" → "moneyline"
 */
function principalMarketIdBase(marketId: string): string {
    return marketId
        .toLowerCase()
        .trim()
        .replace(/^(?:\d+(?:st|nd|rd|th)_)?(?:half|quarter|period)_/, "");
}

/** Exact FG / period board market_ids (the 4 columns + halves/quarters/periods). */
const PRINCIPAL_MARKET_IDS = new Set([
    "moneyline",
    "point_spread",
    "run_line",
    "puck_line",
    "total",
    "total_points",
    "total_runs",
    "total_goals",
    "team_total",
    "team_total_points",
    "team_total_runs",
    "team_total_goals",
]);

/**
 * Principal board markets — evaluated ONLY by market_id (exact match).
 * Names are ignored: they are too ambiguous and blocked specialty props.
 */
export function isPrincipalBoardMarket(_marketName?: string | null, marketId?: string | null): boolean {
    const id = String(marketId ?? "").toLowerCase().trim();
    if (!id) return false;
    if (
        id.includes("alternate") ||
        id.includes("player") ||
        id.includes("batter") ||
        id.includes("pitcher")
    ) {
        return false;
    }
    const base = principalMarketIdBase(id);
    return PRINCIPAL_MARKET_IDS.has(base) || PRINCIPAL_MARKET_IDS.has(id);
}

/** True only for real prop tickets (not principal board markets tagged as props by mistake). */
export function isPropsBet(bet: {
    betType?: string;
    gameType?: string;
    marketName?: string;
    odd?: { market?: string; market_id?: string };
}): boolean {
    const gameType = String(bet.gameType ?? "").toLowerCase();
    if (gameType !== "props" && gameType !== "propslive") return false;
    return !isPrincipalBoardMarket(undefined, bet.odd?.market_id);
}

/**
 * Resolve gameType when placing from the props screen.
 * Default = "props" / "propsLive". Only exact principal market_ids skip it.
 */
export function resolvePropsGameType(
    _marketName?: string | null,
    type?: string | null,
    marketId?: string | null,
): string | undefined {
    const isLiveType = type === "propsLive" || type === "live";

    if (isPrincipalBoardMarket(undefined, marketId)) {
        return isLiveType ? "live" : undefined;
    }

    return isLiveType ? "propsLive" : "props";
}

const DRAW_LABEL_RE = /\b(draw|tie|empate)\b/i;

export function isDrawOdd(odd: Odd): boolean {
    return [odd.selection, odd.name, odd.normalized_selection]
        .some(value => Boolean(value) && DRAW_LABEL_RE.test(value));
}

export const PRIMARY_SPORTSBOOK = "BetRivers";
export const SECONDARY_SPORTSBOOK = "Fanatics";
/** Last parlay-price fallback. Not used on the straight board. */
export const TERTIARY_SPORTSBOOK = "DraftKings";

export const ACTIVE_SPORTSBOOKS = [PRIMARY_SPORTSBOOK, SECONDARY_SPORTSBOOK];

const SPORTSBOOK_PRIORITY = [
    PRIMARY_SPORTSBOOK.toLowerCase(),
    SECONDARY_SPORTSBOOK.toLowerCase(),
];

export function pickPreferredSportsbook<T extends { sportsbook?: string | null }>(
    odds: T[],
): string | undefined {
    for (const key of SPORTSBOOK_PRIORITY) {
        const match = odds.find(odd => odd.sportsbook?.trim().toLowerCase() === key);
        if (match?.sportsbook) return match.sportsbook;
    }
    return odds.find(odd => odd.sportsbook)?.sportsbook;
}

/** Alternate lines for the clicked side only (team, player, and over/under). */
export function isSameSelectionSide(
    selected: { player_id?: string | null; team_id?: string | null; selection_line?: string | null },
    odd: { player_id?: string | null; team_id?: string | null; selection_line?: string | null },
): boolean {
    if (selected.player_id) {
        if (odd.player_id !== selected.player_id) return false;
    } else if (selected.team_id) {
        if (odd.team_id !== selected.team_id) return false;
    }

    if (selected.selection_line) {
        return odd.selection_line === selected.selection_line;
    }

    return Boolean(selected.player_id || selected.team_id);
}

export function filterPreferredSportsbookOdds<T extends { sportsbook?: string | null }>(odds: T[]): T[] {
    const book = pickPreferredSportsbook(odds);
    if (!book) return odds;
    return odds.filter(odd => odd.sportsbook === book);
}

/** Per market: BetRivers, then Fanatics, then the first book present. */
export function preferPrimarySportsbookOdds(odds: Odd[]): Odd[] {
    if (odds.length === 0) return odds;

    const byMarket = Object.groupBy(odds, odd => odd.market_id || odd.market || "unknown");
    return Object.values(byMarket).flatMap(group => {
        if (!group?.length) return [];
        return filterPreferredSportsbookOdds(group);
    });
}

/** Full-game board columns only (not 1st half / quarter / inning markets). */
export function isExactFgBoardMarket(marketId?: string | null): boolean {
    const id = String(marketId ?? "").toLowerCase().trim();
    return PRINCIPAL_MARKET_IDS.has(id);
}
/** Extra board row: Draw only, BetRivers first per market. */
export function getDrawOddsGroup(odds: Odd[] | undefined): Odd[][] {
    const draws = preferPrimarySportsbookOdds((odds ?? []).filter(isDrawOdd));
    return draws.length ? [draws] : [];
}

function lastWords(text: string, count: number): string {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (words.length <= count) return words.join(" ");
    return words.slice(-count).join(" ");
}

function displayNamesCollide(left: string, right: string): boolean {
    return left.trim().toLowerCase() === right.trim().toLowerCase();
}

const CLUB_SUFFIXES = new Set(["fc", "afc", "cfc", "cf", "sc", "ac", "fk", "sk"]);

function stripClubSuffixes(text: string): string {
    const words = text.trim().split(/\s+/).filter(Boolean);
    while (
        words.length > 1 &&
        CLUB_SUFFIXES.has(words[words.length - 1].replace(/\./g, "").toLowerCase())
    ) {
        words.pop();
    }
    return words.join(" ");
}

function dropSharedLastWord(text: string, sharedLast: string): string {
    const words = text.trim().split(/\s+/).filter(Boolean);
    if (
        words.length >= 2 &&
        words[words.length - 1].toLowerCase() === sharedLast.toLowerCase()
    ) {
        return words.slice(0, -1).join(" ");
    }
    return words.join(" ");
}

/** Short labels for a matchup; if both collapse to City/United, use Hull vs Coventry. */
export function formatMatchupPair(
    left?: string | null,
    right?: string | null,
    league?: string,
    joiner = " vs ",
): string {
    const aRaw = String(left ?? "").trim();
    const bRaw = String(right ?? "").trim();
    if (!aRaw && !bRaw) return "";
    if (!bRaw) return getName(aRaw, league);
    if (!aRaw) return getName(bRaw, league);

    if (isSoccerLeague(league)) {
        return `${aRaw}${joiner}${bRaw}`;
    }

    if (shouldUseFullTeamName(league)) {
        return `${compactCollegeTeamName(aRaw)}${joiner}${compactCollegeTeamName(bRaw)}`;
    }

    const aClean = stripClubSuffixes(aRaw);
    const bClean = stripClubSuffixes(bRaw);

    let a = shortenTeamDisplayName(aClean, false);
    let b = shortenTeamDisplayName(bClean, false);
    if (displayNamesCollide(a, b) && a) {
        a = dropSharedLastWord(aClean, a);
        b = dropSharedLastWord(bClean, b);
    }
    if (displayNamesCollide(a, b)) {
        a = lastWords(aClean, 2);
        b = lastWords(bClean, 2);
    }
    if (displayNamesCollide(a, b)) {
        a = aClean;
        b = bClean;
    }
    return `${a}${joiner}${b}`;
}

export const getName = (text?: string, league?: string) => {
    if (!text) return "";

    const useFullName = shouldUseFullTeamName(league);
    const keepSoccerFullName = isSoccerLeague(league);

    if (text.includes(" vs ")) {
        const matchup = text.split(" - ")[0];

        const teams = matchup.split(" vs ");

        if (teams.length === 2) {
            if (useFullName || keepSoccerFullName) {
                return `${teams[0].trim()} vs ${teams[1].trim()}`;
            }
            return formatMatchupPair(teams[0], teams[1], league);
        }
    }

    const words = text.trim().split(/\s+/).filter(Boolean);

    if (words.length === 0) return "";
    if (words.length === 1) return words[0];

    const overIndex = words.findIndex(
        w => /^over$/i.test(w) || /^under$/i.test(w)
    );

    if (overIndex > 0) {
        return words.slice(0, overIndex).join(" ");
    }

    const last = words[words.length - 1];
    const secondLast = words[words.length - 2];

    const hasSpreadSuffix =
        /^[-+]\d+(?:\.\d+)?$/.test(last) ||
        ((secondLast === "-" || secondLast === "+") && /^\d+(?:\.\d+)?$/.test(last));

    if (hasSpreadSuffix) {
        const teamWords =
            (secondLast === "-" || secondLast === "+") && /^\d+(?:\.\d+)?$/.test(last)
                ? words.slice(0, -2)
                : words.slice(0, -1);
        if (keepSoccerFullName) return teamWords.join(" ");
        return shortenTeamDisplayName(teamWords.join(" "), useFullName);
    }

    if (keepSoccerFullName) return words.join(" ");
    return shortenTeamDisplayName(words.join(" "), useFullName);
};

/** True when a stored "team" label is actually Over/Under or a total line (e.g. "12.5"). */
const isSelectionLabel = (text?: string): boolean => {
    if (!text) return true;
    const t = text.trim().toLowerCase();
    if (t === "over" || t === "under") return true;
    return /^\d+(\.\d+)?$/.test(t);
};

type TeamCompetitorSide = {
    id?: string | number | null;
    name?: string;
    abbreviation?: string;
    logo?: string;
} | null | undefined;

const normalizeTeamKey = (value?: string | number | null): string =>
    String(value ?? "").trim().toLowerCase().replace(/[^a-z0-9]+/g, "");

function competitorMatchesOdd(
    team: TeamCompetitorSide,
    odd?: {
        team_id?: string | number | null;
        selection?: string | null;
        name?: string;
        normalized_selection?: string;
    } | null,
    by: "name" | "id" = "name",
): boolean {
    if (!team || !odd) return false;

    if (by === "id") {
        const oddTeamId = normalizeTeamKey(odd.team_id);
        return Boolean(oddTeamId && oddTeamId === normalizeTeamKey(team.id));
    }

    const selection = normalizeTeamKey(odd.selection || odd.normalized_selection);
    const oddName = normalizeTeamKey(odd.name);
    const teamName = normalizeTeamKey(team.name);
    const abbr = normalizeTeamKey(team.abbreviation);

    if (selection && /^(yes|no)$/.test(selection)) return false;
    if (selection && teamName && (selection === teamName || selection === abbr || teamName.startsWith(selection) || selection.startsWith(teamName))) {
        return true;
    }
    if (oddName && teamName && (oddName === teamName || oddName.startsWith(teamName))) {
        return true;
    }
    return false;
}

/** Map an odd to the selected team vs opponent. Never assume home is the pick. */
export function resolveSelectedTeams(
    odd: {
        team_id?: string | number | null;
        selection?: string | null;
        name?: string;
        normalized_selection?: string;
    } | null | undefined,
    home?: TeamCompetitorSide,
    away?: TeamCompetitorSide,
): { teamId: string; opposingTeam: string; selectedLogo?: string } {
    const homeName = home?.name ?? "";
    const awayName = away?.name ?? "";

    if (competitorMatchesOdd(home, odd, "name")) {
        return { teamId: homeName, opposingTeam: awayName, selectedLogo: home?.logo };
    }
    if (competitorMatchesOdd(away, odd, "name")) {
        return { teamId: awayName, opposingTeam: homeName, selectedLogo: away?.logo };
    }
    if (competitorMatchesOdd(home, odd, "id")) {
        return { teamId: homeName, opposingTeam: awayName, selectedLogo: home?.logo };
    }
    if (competitorMatchesOdd(away, odd, "id")) {
        return { teamId: awayName, opposingTeam: homeName, selectedLogo: away?.logo };
    }

    return { teamId: homeName, opposingTeam: awayName };
}

/** Contest odd names often include "Over 7.5" / "Under 7.5"; the O/U line is shown separately. */
export function getContestSelectionName(text?: string): string {
    if (!text) return "";
    const words = text.trim().split(/\s+/).filter(Boolean);
    const overIndex = words.findIndex((w) => /^over$/i.test(w) || /^under$/i.test(w));
    if (overIndex > 0) {
        return words.slice(0, overIndex).join(" ");
    }
    return words.join(" ");
}

/** Team the user actually picked in the modal (not the home row / stored teamId). */
export function getBetTeamPickName(bet: {
    league?: string;
    teamId?: string;
    betName?: string;
    betType?: string;
    marketName?: string;
    odd?: { selection?: string | null; name?: string; market?: string };
}): string {
    if (bet.betType === "Contest") {
        return getContestSelectionName(bet.betName || bet.odd?.name || bet.teamId);
    }
    const name = getName(bet.odd?.selection || bet.betName || bet.odd?.name || bet.teamId, bet.league);
    return `${name}${getPeriodTeamSuffix(bet.marketName || bet.odd?.market, bet.league)}`;
}

/**
 * Primary label for a bet/selection row: team name for team markets,
 * full matchup for game totals (avoids "Under vs Marlins" / "12.5 vs Marlins").
 */
export const getBetPrimaryName = (bet: {
    league?: string;
    teamId?: string;
    opposingTeam?: string;
    betName?: string;
    betType?: string;
    marketName?: string;
    odd?: { name?: string; selection?: string | null; market?: string; team_id?: string | number | null; player_id?: string | number | null };
    grader?: { away_team_display?: string; home_team_display?: string };
}): string => {
    if (bet.betType === "Contest") {
        return getContestSelectionName(bet.betName || bet.odd?.name || bet.teamId);
    }

    if (bet.odd?.player_id) {
        return getPlayerName(bet.betName ?? bet.odd?.name ?? "");
    }

    const periodSuffix = getPeriodTeamSuffix(bet.marketName || bet.odd?.market, bet.league);

    if (bet.odd?.team_id) {
        return getBetTeamPickName(bet);
    }

    const away = bet.grader?.away_team_display;
    const home = bet.grader?.home_team_display;
    if (away && home) {
        const fromGrader = formatMatchupPair(away, home, bet.league);
        const [left, right] = fromGrader.split(" vs ");
        if (
            left && right && displayNamesCollide(left, right) &&
            bet.teamId && bet.opposingTeam && !isSelectionLabel(bet.teamId)
        ) {
            return `${formatMatchupPair(bet.teamId, bet.opposingTeam, bet.league)}${periodSuffix}`;
        }
        return `${fromGrader}${periodSuffix}`;
    }

    if (bet.teamId && bet.opposingTeam && !isSelectionLabel(bet.teamId)) {
        return `${formatMatchupPair(bet.teamId, bet.opposingTeam, bet.league)}${periodSuffix}`;
    }

    if (bet.opposingTeam && !isSelectionLabel(bet.opposingTeam)) {
        return `${getName(bet.opposingTeam, bet.league)}${periodSuffix}`;
    }

    return `${getName(bet.odd?.name ?? bet.teamId, bet.league)}${periodSuffix}`;
};

/** Matchup for expanded "Game" rows: Away / Home when possible. */
export const getBetMatchupLabel = (bet: {
    league?: string;
    teamId?: string;
    opposingTeam?: string;
    betName?: string;
    odd?: { name?: string; team_id?: string | number | null; player_id?: string | number | null; market?: string };
    grader?: { away_team_display?: string; home_team_display?: string };
}): string => {
    if (bet.odd?.player_id) {
        return bet.odd?.market ?? getBetPrimaryName(bet);
    }

    const away = bet.grader?.away_team_display;
    const home = bet.grader?.home_team_display;
    if (away && home) {
        return formatMatchupPair(away, home, bet.league, " / ");
    }

    if (bet.teamId && bet.opposingTeam && !isSelectionLabel(bet.teamId)) {
        return formatMatchupPair(bet.opposingTeam, bet.teamId, bet.league, " / ");
    }

    if (bet.opposingTeam && bet.odd?.name && !isSelectionLabel(bet.odd.name)) {
        return formatMatchupPair(bet.opposingTeam, bet.odd.name, bet.league, " / ");
    }

    return getBetPrimaryName(bet);
};

export const capitalize = (text?: string) => text ? text.charAt(0).toUpperCase() + text.slice(1) : '';

export const getLiveLabel = (gameType?: string | null): string => {
    if (!gameType) return '';

    return gameType.toLowerCase().includes('live') ? 'Live' : '';
};

/**
 * Secondary props label under the primary name:
 * - player/team props → market only
 * - game props (Yes/No, etc.) → "selection / market"
 */
export function getPropsMarketLabel(bet: {
    betName?: string;
    marketName?: string;
    odd?: {
        market?: string;
        selection?: string | null;
        name?: string;
        team_id?: string | number | null;
        player_id?: string | number | null;
    };
}): string {
    const market = bet.marketName || bet.odd?.market || '';
    if (bet.odd?.player_id || bet.odd?.team_id) {
        return market;
    }
    const selection = String(bet.odd?.selection || bet.betName || bet.odd?.name || '').trim();
    return [selection, market].filter(Boolean).join(' / ');
}

/** Human-readable specialty market name (e.g. "1st 7 Innings Total Runs"). */
export function formatSpecialtyMarketLabel(raw?: string | null): string {
    if (!raw) return "";
    const text = raw.trim().replace(/_/g, " ");
    if (!text) return "";

    return text.replace(/\b([A-Za-z0-9]+)\b/g, (word) => {
        if (/^\d+(st|nd|rd|th)$/i.test(word)) return word.toLowerCase();
        const lower = word.toLowerCase();
        if (lower === "ml") return "ML";
        if (lower === "tt") return "TT";
        if (lower === "vs") return "vs";
        return lower.charAt(0).toUpperCase() + lower.slice(1);
    });
}

type BetSelectionSource = {
    betName?: string;
    marketName?: string;
    betType?: string;
    gameType?: string;
    odd?: {
        market?: string;
        market_id?: string;
        selection?: string | null;
        name?: string;
        player_id?: string | number | null;
        team_id?: string | number | null;
    };
};

/** Picked outcome + market for the modal (e.g. "No / Will There Be Overtime"). */
export function getBetSelectionLabel(bet: BetSelectionSource | null | undefined): string {
    const market = formatSpecialtyMarketLabel(bet?.marketName || bet?.odd?.market);
    if (!bet?.odd) return market;

    const rawSelection = String(bet.odd.selection || "").trim();
    const fallbackSelection = !bet.odd.player_id && !bet.odd.team_id
        ? String(bet.betName || bet.odd.name || "").trim()
        : "";
    const selection = rawSelection || fallbackSelection;

    if (!selection) return market;
    if (!market) return selection;
    if (selection.toLowerCase() === market.toLowerCase()) return market;

    const isYesNo = /^(yes|no)$/i.test(selection);
    const isGameProp =
        !bet.odd.player_id &&
        !bet.odd.team_id &&
        !isPrincipalBoardMarket(bet.marketName, bet.odd.market_id);

    if (isYesNo || isGameProp) {
        return `${selection} / ${market}`;
    }

    return market;
}

/** Label shown under the bet line for non-mainline markets (not spread, ML, total, or TT). */
export function getBetSpecialtyMarketLabel(bet: BetSelectionSource | null | undefined): string {
    if (!bet?.odd) return "";

    if (bet.betType === "Contest") {
        return formatSpecialtyMarketLabel(bet.marketName || bet.odd.market);
    }

    if (bet.odd.player_id) return "";

    if (isExactFgBoardMarket(bet.odd.market_id)) {
        return "";
    }

    return getBetSelectionLabel(bet);
}

/** Grey subtitle under pending/history: market for non-regular bets. */
export function getPendingMarketSubLabel(bet: BetSelectionSource | null | undefined): string {
    if (!bet) return "";
    if (bet.betType === "Contest" || isPropsBet(bet)) {
        return getPropsMarketLabel(bet);
    }
    return getBetSpecialtyMarketLabel(bet);
}

/** Full player or team name, without the short display used on the bet row. */
function getFullSelectionName(bet: {
    betName?: string;
    teamId?: string;
    odd?: { name?: string };
} | null | undefined): string {
    const raw = String(bet?.betName || bet?.odd?.name || bet?.teamId || "").trim();
    if (!raw) return "";

    const words = raw.split(/\s+/).filter(Boolean);
    const overIndex = words.findIndex(word => /^(over|under)$/i.test(word));
    const nameWords = overIndex > 0 ? words.slice(0, overIndex) : [...words];

    while (nameWords.length > 0 && /^[-+]$/.test(nameWords[nameWords.length - 1])) {
        nameWords.pop();
    }
    while (nameWords.length > 0 && /^[-+]?\d+(?:\.\d+)?$/.test(nameWords[nameWords.length - 1])) {
        nameWords.pop();
    }

    return nameWords.join(" ");
}

/** Admin pending detail: full player or team name plus the market. */
export function getAdminPendingSelectionLabel(bet: Parameters<typeof getBetPrimaryName>[0] | null | undefined): string {
    if (!bet) return "";

    const picked = getFullSelectionName(bet);
    const market = formatSpecialtyMarketLabel(bet.marketName || bet.odd?.market);
    const sideRaw = String(bet.odd?.selection || "").trim();
    const side = /^(over|under|yes|no)$/i.test(sideRaw)
        ? sideRaw.charAt(0).toUpperCase() + sideRaw.slice(1).toLowerCase()
        : "";

    const who = [picked, side].filter((part, index, parts) =>
        Boolean(part) && parts.findIndex(item => item.toLowerCase() === part.toLowerCase()) === index
    );

    if (who.length === 0) return market;
    if (!market || who.join(" ").toLowerCase() === market.toLowerCase()) return who.join(" ");
    return `${who.join(" ")} — ${market}`;
}

export const isFreePlayBet = (bet: { betType?: string; freeBet?: string; isFreePlay?: boolean }): boolean =>
    bet.betType === "Free Play" || bet.freeBet === "FP" || bet.isFreePlay === true;


//BetMarketsDelayed

const delayedMarkets = [
    "1st half point spread",
    "1st half moneyline",
    "1st half total points",
    "1st half team total",
    "1st half run line",
    "1st quarter point spread",
    "1st quarter moneyline",
    "1st quarter total points",
    "1st quarter team total",
    "1st quarter run line",
];

export const shouldShowDelayedBetMessage = (bet: any) => {
    if (!bet) return false;

    const betType = String(bet?.betType ?? "").toLowerCase();
    if (betType.includes("parlay")) return false;

    const isContestDelayed =
        betType === "contest" &&
        bet?.sport?.toLowerCase() !== "golf";

    const isMarketDelayed =
        bet?.betType !== "Contest" &&
        delayedMarkets.includes(
            bet?.odd?.market?.toLowerCase() || ""
        );

    return isContestDelayed || isMarketDelayed;
};

/** Normalize MLB starter values (string or API object) to a display name. */
export function getPitcherName(starter: unknown): string {
    if (!starter) return "";
    if (typeof starter === "string") return starter.trim();
    if (typeof starter === "object") {
        const value = starter as { name?: string; full_name?: string; display?: string };
        return String(value.name ?? value.full_name ?? value.display ?? "").trim();
    }
    return String(starter).trim();
}

/**
 * MLB pitchers payload for bets.
 * `away` / `home` = whether that pitcher checkbox was selected.
 */
export function buildMlbPitchers(params: {
    league?: string | null;
    marketName?: string | null;
    pitcherAway?: unknown;
    pitcherHome?: unknown;
    pitcherAwayId?: string | null;
    pitcherHomeId?: string | null;
    checkedAway?: boolean;
    checkedHome?: boolean;
}): BetDetails["pitchers"] | undefined {
    const league = String(params.league ?? "").trim();
    if (league !== "MLB") return undefined;

    const awayName = getPitcherName(params.pitcherAway);
    const homeName = getPitcherName(params.pitcherHome);
    const awayId = String(params.pitcherAwayId ?? "").trim();
    const homeId = String(params.pitcherHomeId ?? "").trim();

    return {
        away: !!params.checkedAway,
        home: !!params.checkedHome,
        names: {
            away: awayName || "TBD",
            home: homeName || "TBD",
        },
        ids: {
            away: awayId,
            home: homeId,
        },
    };
}

/** Selected MLB pitcher names only (checkbox true). */
export function formatSelectedMlbPitchers(
    pitchers?: BetDetails["pitchers"] | null
): string | null {
    if (!pitchers) return null;
    const parts: string[] = [];
    if (pitchers.away && pitchers.names?.away?.trim()) {
        parts.push(pitchers.names.away.trim());
    }
    if (pitchers.home && pitchers.names?.home?.trim()) {
        parts.push(pitchers.names.home.trim());
    }
    return parts.length > 0 ? parts.join(" / ") : null;
}
